import { useAuditLogFilterOptions } from "../../../../hooks/queries/admin/auditLogs";
import { AdminDateField } from "../AdminDateField";
import { CheckboxField } from "../AdminPage.styles";
import { AdminSelectField } from "../AdminSelectField";
import {
  AUDIT_FILTER_FIELD_HEIGHT,
  AuditFilterButton,
  AuditFilterField,
  AuditFilterInput,
  AuditFilterLabel,
  AuditFiltersHint,
  AuditFiltersRow,
  AuditWindowNav,
} from "./AuditLog.styles";
import {
  auditWindowError,
  IAuditLogWindow,
  isLatestAuditWindow,
  MAX_AUDIT_WINDOW_DAYS,
  OPERATION_LABELS,
  shiftAuditWindow,
} from "./auditLogWindow";

/** What the History tabs filter on besides the window. */
export interface IAuditLogPanelFilterState {
  /** One operation, or "" for all. */
  operation: string;
  source: string;
  applicationUserEmail: string;
  /** Leave READ rows out when no operation is chosen. */
  hideViews: boolean;
}

export const DEFAULT_PANEL_FILTERS: IAuditLogPanelFilterState = {
  operation: "",
  source: "",
  applicationUserEmail: "",
  hideViews: true,
};

interface IAuditLogPanelFiltersProps {
  /** Prefixes the field ids, so two panels on one page don't share them. */
  idPrefix: string;
  auditWindow: IAuditLogWindow;
  onWindowChange: (next: IAuditLogWindow) => void;
  /** Omitted where the endpoint only takes a window (survey answer history). */
  filters?: IAuditLogPanelFilterState;
  onFiltersChange?: (filters: IAuditLogPanelFilterState) => void;
}

/**
 * Filter bar of the History tabs (EN-5288): a window of at most 90 days that
 * can step back through older history, plus operation, source and user.
 */
export function AuditLogPanelFilters({
  idPrefix,
  auditWindow,
  onWindowChange,
  filters,
  onFiltersChange,
}: IAuditLogPanelFiltersProps) {
  // Same lists as the Audit Logs page; cached for an hour, so tabs share one request.
  const { filterOptions } = useAuditLogFilterOptions();
  const windowError = auditWindowError(auditWindow);
  const setFilter = <K extends keyof IAuditLogPanelFilterState>(
    key: K,
    value: IAuditLogPanelFilterState[K]
  ) => filters && onFiltersChange?.({ ...filters, [key]: value });

  return (
    <>
      <AuditFiltersRow>
        <AuditFilterField>
          <AuditFilterLabel htmlFor={`${idPrefix}-from`}>From</AuditFilterLabel>
          <AdminDateField
            id={`${idPrefix}-from`}
            value={auditWindow.startDate}
            onChange={(startDate) =>
              onWindowChange({ ...auditWindow, startDate })
            }
          />
        </AuditFilterField>

        <AuditFilterField>
          <AuditFilterLabel htmlFor={`${idPrefix}-to`}>To</AuditFilterLabel>
          <AdminDateField
            id={`${idPrefix}-to`}
            value={auditWindow.endDate}
            onChange={(endDate) => onWindowChange({ ...auditWindow, endDate })}
          />
        </AuditFilterField>

        <AuditWindowNav>
          <AuditFilterButton
            type="button"
            variant="secondary"
            disabled={Boolean(windowError)}
            onClick={() => onWindowChange(shiftAuditWindow(auditWindow, -1))}
          >
            ← Older
          </AuditFilterButton>
          <AuditFilterButton
            type="button"
            variant="secondary"
            disabled={Boolean(windowError) || isLatestAuditWindow(auditWindow)}
            onClick={() => onWindowChange(shiftAuditWindow(auditWindow, 1))}
          >
            Newer →
          </AuditFilterButton>
        </AuditWindowNav>

        {filters && (
          <>
            <AuditFilterField>
              <AuditFilterLabel htmlFor={`${idPrefix}-operation`}>
                Action
              </AuditFilterLabel>
              <AdminSelectField
                id={`${idPrefix}-operation`}
                height={AUDIT_FILTER_FIELD_HEIGHT}
                value={filters.operation}
                onChange={(e) => setFilter("operation", e.target.value)}
              >
                <option value="">All actions</option>
                {filterOptions?.operations?.map((operation) => (
                  <option key={operation} value={operation}>
                    {OPERATION_LABELS[operation] ?? operation}
                  </option>
                ))}
              </AdminSelectField>
            </AuditFilterField>

            <AuditFilterField>
              <AuditFilterLabel htmlFor={`${idPrefix}-source`}>
                Source
              </AuditFilterLabel>
              <AdminSelectField
                id={`${idPrefix}-source`}
                height={AUDIT_FILTER_FIELD_HEIGHT}
                value={filters.source}
                onChange={(e) => setFilter("source", e.target.value)}
              >
                <option value="">All sources</option>
                {filterOptions?.sources?.map((source) => (
                  <option key={source} value={source}>
                    {source}
                  </option>
                ))}
              </AdminSelectField>
            </AuditFilterField>

            <AuditFilterField>
              <AuditFilterLabel htmlFor={`${idPrefix}-user`}>
                User email
              </AuditFilterLabel>
              <AuditFilterInput
                id={`${idPrefix}-user`}
                type="text"
                placeholder="Full or partial email"
                value={filters.applicationUserEmail}
                onChange={(e) =>
                  setFilter("applicationUserEmail", e.target.value)
                }
              />
            </AuditFilterField>

            <CheckboxField>
              <input
                type="checkbox"
                checked={filters.hideViews}
                // A chosen action already decides whether views show.
                disabled={Boolean(filters.operation)}
                onChange={(e) => setFilter("hideViews", e.target.checked)}
              />
              Hide views
            </CheckboxField>
          </>
        )}
      </AuditFiltersRow>

      <AuditFiltersHint error={Boolean(windowError)}>
        {windowError ??
          `Showing up to ${MAX_AUDIT_WINDOW_DAYS} days at a time. Use Older to go further back.`}
      </AuditFiltersHint>
    </>
  );
}
